import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  InputBase,
  alpha,
  useMediaQuery,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useMemo, useState } from 'react';
import { Link as RouterLink, generatePath } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import theme from '../../assets/styles/theme';
import Error from '../Error';
import Loader from '../Loader';
import SectionTitle from '../SectionTitle';
import Typography from '../Typography';

import data from './data.json';

import appStrings from '@/assets/constants/appStrings-no';
import routes from '@/assets/constants/routes-no';

const useStyles = makeStyles()(
  (
    { breakpoints, palette, shadows, spacing, typography },
    _params,
    classes: any,
  ) => ({
    faqContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    // search
    searchInput: {
      // @ts-expect-error
      ...typography.p1,
      padding: spacing(1.5, 0),
      [breakpoints.up('md')]: {
        padding: spacing(2, 0),
      },
    },
    searchInputRoot: {
      boxShadow: shadows[24],
      padding: spacing(0.5, 1.5, 0.5, 3),
      margin: spacing(0, 'auto', 3.5),
      maxWidth: 700,
      borderRadius: 40,
      color: palette.text.default,
      backgroundColor: palette.background.paper,
    },
    inputAdornment: {
      margin: spacing(0, -0.75, 0, 0.5),
      minHeight: 42,
      width: 42,
      minWidth: 42,
      justifyContent: 'center',
      borderRadius: '50%',
      backgroundColor: palette.secondary.main,
      [breakpoints.up('md')]: {
        minHeight: 52,
        width: 52,
        minWidth: 52,
        '& svg': { fontSize: '2rem' },
      },
    },
    // category filter
    filterContainer: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      margin: spacing(0, 'auto', 3.5),
      maxWidth: 300,
      [breakpoints.up('sm')]: {
        maxWidth: 'none',
      },
    },
    filterButton: {
      color: alpha(palette.text.default, 0.5),
      padding: spacing(0.5, 1),
      [`&.${classes.isActive}`]: {
        color: palette.text.default,
        fontWeight: typography.fontWeightBold,
      },
    },
    isActive: {},
    filterDivider: {
      margin: spacing('auto', 0.5),
      backgroundColor: alpha(palette.text.default, 0.5),
      height: '60%',
    },
    // faq items
    faqItemsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'left',
      [breakpoints.up('sm')]: {
        justifyContent: 'center',
      },
    },
    noResultsText: {
      width: '100%',
      textAlign: 'center',
    },
    faqItem: {
      margin: spacing(1),
    },
  }),
);

const categories = [
  {
    key: 1,
    label: appStrings.PRODUCTS.REFINANCING,
    value: appStrings.PRODUCTS.REFINANCING,
  },
  { key: 3, label: appStrings.FAQ.ALL_PRODUCTS, value: undefined },
];

interface FaqListProps {
  category?: 'Mobil' | 'Refinansiering';
}

const FaqList = ({ category = undefined }: FaqListProps) => {
  const { classes, cx } = useStyles();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));

  // category filter
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    category,
  );

  // data download
  // const { data, error, loading } = useGetPostByCategoryQuery(categoryFilter);
  const loading = false;
  const error = null;

  // search filter
  const [searchValue, setSearchValue] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const preparedData =
    data && data.length && searchFilter.length > 1
      ? data.filter(({ title }) =>
          title.toLowerCase().includes(searchFilter.toLowerCase()),
        )
      : data || [];

  const searchItems = useMemo(
    () =>
      debounce(async (searchText: string) => {
        setSearchFilter(searchText);
      }, 600),
    [],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    searchItems(e.target.value);
  };

  return (
    <div className={classes.faqContainer}>
      <SectionTitle title={appStrings.FAQ.TITLE} />

      {category ? null : (
        <div className={classes.filterContainer}>
          {categories.map((item, index) => (
            <Box display="flex" key={item.label}>
              <Button
                variant="text"
                size="large"
                disabled={loading}
                onClick={() => setCategoryFilter(item.value)}
                className={cx(
                  classes.filterButton,
                  categoryFilter === item.value && classes.isActive,
                )}
              >
                {item.label}
              </Button>
              {(isSm ? index + 1 !== categories.length : index % 2 === 0) && (
                <Divider
                  orientation="vertical"
                  className={classes.filterDivider}
                />
              )}
            </Box>
          ))}
        </div>
      )}

      <InputBase
        placeholder={appStrings.FAQ.SEARCH_PLACEHOLDER}
        inputProps={{ 'aria-label': appStrings.FAQ.SEARCH_LABEL }}
        value={searchValue}
        onChange={handleSearch}
        classes={{ root: classes.searchInputRoot, input: classes.searchInput }}
        fullWidth
        endAdornment={
          <InputAdornment
            classes={{ root: classes.inputAdornment }}
            position="end"
          >
            <SearchIcon />
          </InputAdornment>
        }
      />

      <div className={classes.faqItemsContainer}>
        {error && <Error error={error} inline />}
        {loading && !error && <Loader inline />}
        {!loading && !error && (
          <>
            {preparedData.length ? (
              preparedData.map((item) => {
                const product = item.categories.category
                  .find(({ name }) =>
                    [
                      appStrings.PRODUCTS.MOBILE,
                      appStrings.PRODUCTS.REFINANCING,
                    ].includes(name),
                  )
                  ?.name.toLowerCase();
                return (
                  <Button
                    key={item.id}
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={
                      product && item.slug
                        ? generatePath(routes.PRODUCTS_FAQ_ARTICLE, {
                            articleName: item.slug,
                            product,
                          })
                        : '/'
                    }
                    className={classes.faqItem}
                  >
                    {item.title}
                  </Button>
                );
              })
            ) : (
              <Typography variant="p1" className={classes.noResultsText}>
                {appStrings.FAQ.SEARCH_NO_RESULTS}
              </Typography>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FaqList;
